import { useStore } from '@nanostores/react';
import { memo, useState, useCallback, type ChangeEvent, type KeyboardEvent } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogHeader, DialogContent, DialogActions, DialogButton } from '~/components/ui/Dialog';
import { Input } from '~/components/ui/Input';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { settingsService } from '~/lib/services/settings-service';
import { hostingSettingsStore } from '~/lib/stores/settings';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { withSpinner } from '~/utils/withSpinner';

export const MonthlySpendingLimit = memo(() => {
  const hostingSettings = useStore(hostingSettingsStore);
  const monthlySpendingLimit = hostingSettings.monthlySpendingLimit;

  const [innerSpendingLimit, setInnerSpendingLimit] = useState<number | null>(monthlySpendingLimit);
  const [showMonthlySpendingDialog, setShowMonthlySpendingDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleCloseConfirmation = useCallback(() => {
    if (isSaving) {
      return;
    }

    setShowMonthlySpendingDialog(false);
    setInnerSpendingLimit(monthlySpendingLimit);
  }, [monthlySpendingLimit, isSaving]);

  const handleOpenConfirmation = useCallback(() => {
    setShowMonthlySpendingDialog(true);
  }, []);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();

    if (/^\d*$/.test(value)) {
      setInnerSpendingLimit(value === '' ? null : parseInt(value, 10));
    }
  }, []);

  const handleSaveSpendingLimit = useCallback(async () => {
    if (innerSpendingLimit === null) {
      toast.error('Please enter a valid spending limit');
      return;
    }

    setIsSaving(true);
    await withSpinner({
      action: async () => {
        try {
          await settingsService.set('hosting', 'monthlySpendingLimit', innerSpendingLimit);
          setInnerSpendingLimit(hostingSettingsStore.get().monthlySpendingLimit);
          setShowMonthlySpendingDialog(false);
        } catch (error) {
          console.error('Error saving spending limit:', error);
          toast.error('Error saving spending limit');
        } finally {
          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.MonthlySpendingLimitChanged,
              properties: {
                value: innerSpendingLimit,
              },
            },
          });
        }
      },
      onStateUpdate: setIsSaving,
    });
  }, [innerSpendingLimit]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSaveSpendingLimit();
      }
    },
    [handleSaveSpendingLimit],
  );

  return (
    <>
      <div className="flex justify-between items-center gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-600">Monthly spending limit</h2>

          <p className="max-w-128 text-bolt-ds-textSecondary text-sm">
            You can go over your default spending limit ($0) to upgrade your hosting capacity and control the amount you
            don't want to go over. You will be charged the consumed amount.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm">
            <span className="text-bolt-ds-textSecondary">Current:</span>{' '}
            <span>{monthlySpendingLimit === 0 ? 'Default ($0)' : '$' + monthlySpendingLimit + ' / month'}</span>
          </p>

          <Button variant="link-color" className="text-[13px] leading-[150%]" onClick={handleOpenConfirmation}>
            Change spending limit
          </Button>
        </div>
      </div>

      <Dialog maxWidth={512} open={showMonthlySpendingDialog} onClose={handleCloseConfirmation}>
        <DialogHeader className="border-none" title="Monthly spending limit" />

        <DialogContent className="pt-0 flex flex-col gap-4">
          <p className="text-base">Additional usage will be billed at $0.25 per GB and $0.075 per 10,000 requests.</p>
          <Input
            autoFocus
            inputClassName="bg-bolt-ds-surface"
            type="text"
            value={innerSpendingLimit !== null ? innerSpendingLimit.toString() : ''}
            label="Spending limit"
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            size="sm"
          />
        </DialogContent>

        <DialogActions>
          <DialogButton
            disabled={isSaving}
            className="min-w-20"
            size="md"
            variant="secondary"
            onClick={handleCloseConfirmation}
          >
            Cancel
          </DialogButton>
          <DialogButton
            truncate={false}
            className="min-w-20"
            size="md"
            variant="primary"
            disabled={isSaving || innerSpendingLimit === null}
            onClick={handleSaveSpendingLimit}
          >
            {isSaving ? (
              <>
                <span className="i-svg-spinners:90-ring-with-bg size-4 inline-block" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </DialogButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

MonthlySpendingLimit.displayName = 'MonthlySpendingLimit';
